/**
 * Tickets Slice
 * Manages ticket state and async operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ticketsApi } from '../../utils/api/index.js';
import { extractPaginatedList, normalizePagination } from '../../utils/pagination.js';
import { getErrorMessage } from '../../utils/api/errors.js';

/**
 * Initial state
 */
const initialState = {
  tickets: [],
  currentTicket: null,
  stats: null,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    status: null,
    priority: null,
    search: '',
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

/**
 * Async thunk: Fetch all tickets
 */
export const fetchTickets = createAsyncThunk(
  'tickets/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch tickets'));
    }
  }
);

/**
 * Async thunk: Fetch ticket by ID
 */
export const fetchTicketById = createAsyncThunk(
  'tickets/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch ticket'));
    }
  }
);

/**
 * Async thunk: Fetch ticket by Ticket ID (TKT-XXXX)
 */
export const fetchTicketByTicketId = createAsyncThunk(
  'tickets/fetchByTicketId',
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.getByTicketId(ticketId);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch ticket'));
    }
  }
);

/**
 * Async thunk: Create ticket
 */
export const createTicket = createAsyncThunk(
  'tickets/create',
  async (ticketData, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.create(ticketData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create ticket'));
    }
  }
);

/**
 * Async thunk: Update ticket
 */
export const updateTicket = createAsyncThunk(
  'tickets/update',
  async ({ id, ticketData }, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.update(id, ticketData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update ticket'));
    }
  }
);

/**
 * Async thunk: Delete ticket
 */
export const deleteTicket = createAsyncThunk(
  'tickets/delete',
  async (id, { rejectWithValue }) => {
    try {
      await ticketsApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete ticket'));
    }
  }
);

/**
 * Async thunk: Update ticket status
 */
export const updateTicketStatus = createAsyncThunk(
  'tickets/updateStatus',
  async ({ id, status, note }, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.updateStatus(id, status, note);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update ticket status'));
    }
  }
);

/**
 * Async thunk: Add message to ticket
 */
export const addMessage = createAsyncThunk(
  'tickets/addMessage',
  async ({ ticketId, message, senderName, senderRole }, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.addMessage(ticketId, { message, senderName, senderRole });
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to add message'));
    }
  }
);

/**
 * Async thunk: Fetch ticket statistics
 */
export const fetchTicketStats = createAsyncThunk(
  'tickets/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsApi.getStats();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch statistics'));
    }
  }
);

/**
 * Tickets slice
 */
const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    /**
     * Clear current ticket
     */
    clearCurrentTicket: (state) => {
      state.currentTicket = null;
      state.error = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set filters
     */
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Reset filters
     */
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    /**
     * Set pagination
     */
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    /**
     * Add message to current ticket (for real-time updates)
     */
    addMessageToTicket: (state, action) => {
      const { message } = action.payload;
      if (state.currentTicket) {
        if (!state.currentTicket.messages) {
          state.currentTicket.messages = [];
        }
        state.currentTicket.messages.push(message);
      }
    },

    /**
     * Update ticket in list (for real-time updates)
     */
    updateTicketInList: (state, action) => {
      const { ticket } = action.payload;
      const index = state.tickets.findIndex((t) => t.id === ticket.id);
      if (index !== -1) {
        state.tickets[index] = ticket;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all tickets
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading = false;
        const { data, pagination } = extractPaginatedList(action.payload);
        state.tickets = data;
        if (pagination) {
          state.pagination = normalizePagination(pagination, state.pagination);
        }
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.tickets = [];
      })

      // Fetch ticket by ID
      .addCase(fetchTicketById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTicket = action.payload?.data || action.payload;
      })
      .addCase(fetchTicketById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentTicket = null;
      })

      // Fetch ticket by Ticket ID
      .addCase(fetchTicketByTicketId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketByTicketId.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTicket = action.payload?.data || action.payload;
      })
      .addCase(fetchTicketByTicketId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentTicket = null;
      })

      // Create ticket
      .addCase(createTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.loading = false;
        const newTicket = action.payload?.data || action.payload;
        if (newTicket) {
          state.tickets.unshift(newTicket);
        }
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update ticket
      .addCase(updateTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTicket.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTicket = action.payload?.data || action.payload;
        if (updatedTicket) {
          const index = state.tickets.findIndex((t) => t.id === updatedTicket.id);
          if (index !== -1) {
            state.tickets[index] = updatedTicket;
          }
          if (state.currentTicket?.id === updatedTicket.id) {
            state.currentTicket = updatedTicket;
          }
        }
      })
      .addCase(updateTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete ticket
      .addCase(deleteTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = state.tickets.filter((t) => t.id !== action.payload);
        if (state.currentTicket?.id === action.payload) {
          state.currentTicket = null;
        }
      })
      .addCase(deleteTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update ticket status
      .addCase(updateTicketStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTicket = action.payload?.data || action.payload;
        if (updatedTicket) {
          const index = state.tickets.findIndex((t) => t.id === updatedTicket.id);
          if (index !== -1) {
            state.tickets[index] = updatedTicket;
          }
          if (state.currentTicket?.id === updatedTicket.id) {
            state.currentTicket = updatedTicket;
          }
        }
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add message
      .addCase(addMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMessage.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload?.data || action.payload;
        if (response?.ticket) {
          state.currentTicket = response.ticket;
        }
      })
      .addCase(addMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch statistics
      .addCase(fetchTicketStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload?.data || action.payload;
      })
      .addCase(fetchTicketStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearCurrentTicket,
  clearError,
  setFilters,
  resetFilters,
  setPagination,
  addMessageToTicket,
  updateTicketInList,
} = ticketsSlice.actions;

export default ticketsSlice.reducer;
